import { useEffect, useRef } from "react";
import { getWindowRoleSync, useWindowRole } from "@/desktop/use-window-role";
import { hydrateConversationFrameHistory } from "@/lib/conversation-frame-history-cache";
import { useProjectStore } from "@/stores/project-store";

interface ConversationFrameHistoryArgs {
  enabled?: boolean;
  frameId: string;
  conversationId?: string;
}

export function useConversationFrameHistoryHydration({
  enabled = true,
  frameId,
  conversationId,
}: ConversationFrameHistoryArgs) {
  const windowRole = useWindowRole();
  const currentProjectId = useProjectStore((s) => s.currentProject?.id);
  const currentSpaceId = useProjectStore((s) => s.currentSpace?.id);
  const lastHydrationKeyRef = useRef<string | null>(null);

  // Hydrate on every binding change, even when the frame already shows
  // live messages — the store merges by message id, so full history and
  // live streaming coexist without duplicates. The hydration key keeps a
  // stable binding from re-fetching in a loop.
  useEffect(() => {
    if (
      !enabled ||
      windowRole !== "owner" ||
      !currentProjectId ||
      !currentSpaceId
    )
      return;
    const key = [
      currentProjectId,
      currentSpaceId,
      frameId,
      conversationId ?? "",
    ].join(":");
    if (lastHydrationKeyRef.current === key) return;
    lastHydrationKeyRef.current = key;
    let active = true;
    const shouldContinue = () => {
      const projectState = useProjectStore.getState();
      return (
        active &&
        getWindowRoleSync() === "owner" &&
        projectState.currentProject?.id === currentProjectId &&
        projectState.currentSpace?.id === currentSpaceId
      );
    };

    // Defer the request by one microtask. React Strict Mode immediately
    // replays effects in development; the first setup is cleaned up before
    // this runs, preventing a stale request from occupying the shared
    // in-flight cache and discarding the replay's result.
    void Promise.resolve().then(async () => {
      if (!shouldContinue()) return;
      try {
        await hydrateConversationFrameHistory({
          projectId: currentProjectId,
          spaceId: currentSpaceId,
          frameId,
          ...(conversationId ? { conversationId } : {}),
          shouldContinue,
        });
      } catch {
        if (lastHydrationKeyRef.current === key) {
          lastHydrationKeyRef.current = null;
        }
      }
    });
    return () => {
      active = false;
      if (lastHydrationKeyRef.current === key) {
        lastHydrationKeyRef.current = null;
      }
    };
  }, [
    currentProjectId,
    currentSpaceId,
    conversationId,
    enabled,
    frameId,
    windowRole,
  ]);
}
